import AssociatedContactSelector from '@app/components/AssociatedContactSelector'
import CustomInput from '@app/components/CustomInput'
import FormGroupRenderer, {
  ILabelColumnSize,
} from '@app/components/FormGroupRenderer'
import { AssociatedContact } from '@app/models/AssociatedContact'
import {
  BrokeragePipelineCommissionFee,
  IBrokeragePipelineCommissionFee,
} from '@app/models/BrokeragePipelineCommissionFee'
import { IndividualAmount } from '@app/models/IndividualAmount'
import { ICurrentUserState } from '@app/store/modules/account'
import { IGlobalSiteSettingsState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Button, Input, InputGroup, InputGroupAddon } from 'reactstrap'

interface IPipelineCommissionFeeEditorProps {
  className?: string
  onChange: (newValue: BrokeragePipelineCommissionFee) => void
  style?: React.CSSProperties
  title: string
  value: IBrokeragePipelineCommissionFee
}

type PipelineCommissionFeeEditorProps = IPipelineCommissionFeeEditorProps &
  ICurrentUserState &
  IGlobalSiteSettingsState

export default class PipelineCommissionFeeEditor extends React.PureComponent<
  PipelineCommissionFeeEditorProps
> {
  constructor(props: PipelineCommissionFeeEditorProps) {
    super(props)
    bindFunctions(this)
  }

  public render() {
    if (!this._canEditFeeDistribution()) {
      return null
    }

    const labelColSizes: ILabelColumnSize = {
      md: 7,
      xl: 8,
      xs: 6,
    }

    const totalDistributed = this._getTotalDistributed()

    return (
      <div className={this.props.className} style={this.props.style}>
        <div className='text-center font-weight-bold'>{this.props.title}</div>
        <FormGroupRenderer
          labelText='Total Fee %'
          labelColSizes={labelColSizes}
          name={`${this.props.title}TotalFee`}
          onChange={this._onChangeTotalFeePercent}
          step='0.01'
          type='number'
          value={this.props.value.totalFeePercent}
        />
        <div className='font-weight-bold'>Distribution</div>
        <FormGroupRenderer
          labelText={this.props.siteSettings.brokerageCompany.name}
          labelColSizes={labelColSizes}
          name={`${this.props.title}BrokerageCompanySplit`}
          onChange={this._onChangeCompanySplit}
          step='0.01'
          type='number'
          value={this.props.value.companySplit}
        />
        {this.props.value.individualSplits.toArray().map((ind, i) => (
          <FormGroupRenderer
            key={`individual-split-${i}`}
            label={
              <AssociatedContactSelector
                name={`${this.props.title}IndividualSplit${i + 1}`}
                onChange={(newContact: AssociatedContact) =>
                  this._onChangeIndividualSplitContact(i, newContact)
                }
                required
                value={ind.contact}
              />
            }
            labelColSizes={labelColSizes}
          >
            <InputGroup>
              <CustomInput
                onChange={(e) => this._onChangeIndividualSplitAmount(i, e)}
                step='0.01'
                type='number'
                value={ind.amount}
              />
              <InputGroupAddon addonType='append'>
                <Button
                  color='danger'
                  outline
                  onClick={() => this._deleteIndividualSplit(i)}
                >
                  <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </FormGroupRenderer>
        ))}
        <div className='text-right'>
          <a className='text-primary' onClick={this._addIndividualSplit}>
            Add person to split
          </a>
        </div>
        <FormGroupRenderer
          labelText='Total Distributed'
          labelColSizes={labelColSizes}
        >
          <div className='text-right'>
            {totalDistributed !== 0 && totalDistributed !== 100 && (
              <FontAwesomeIcon
                className='mr-2 text-danger'
                icon={['far', 'exclamation-triangle']}
                fixedWidth
              />
            )}
            {totalDistributed}%
          </div>
        </FormGroupRenderer>
        <div className='font-weight-bold'>Bonus</div>
        {this.props.value.bonuses.toArray().map((ind, i) => (
          <FormGroupRenderer
            key={`bonus-${i}`}
            label={
              <AssociatedContactSelector
                name={`${this.props.title}Bonus${i + 1}`}
                onChange={(newContact: AssociatedContact) =>
                  this._onChangeBonusContact(i, newContact)
                }
                required
                value={ind.contact}
              />
            }
            labelColSizes={labelColSizes}
          >
            <InputGroup>
              <Input
                onChange={(e) =>
                  this._onChangeBonusAmount(i, e.target.valueAsNumber)
                }
                step='0.01'
                type='number'
                value={ind.amount}
              />
              <InputGroupAddon addonType='append'>
                <Button
                  color='danger'
                  outline
                  onClick={() => this._deleteBonus(i)}
                >
                  <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </FormGroupRenderer>
        ))}
        <div className='text-right'>
          <a className='text-primary' onClick={this._addBonus}>
            Add bonus
          </a>
        </div>
      </div>
    )
  }

  private _addIndividualSplit() {
    const newSplit = this.props.value.individualSplits.push(
      new IndividualAmount(),
    )
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).set(
      'individualSplits',
      newSplit,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _addBonus() {
    const newBonus = this.props.value.bonuses.push(new IndividualAmount())
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).set(
      'bonuses',
      newBonus,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _canEditFeeDistribution() {
    return (
      this.props.user &&
      (this.props.user.permissions.isAdmin ||
        this.props.user.permissions.editFeeDistribution === true)
    )
  }

  private _deleteIndividualSplit(index: number) {
    const newValue = (this.props
      .value as BrokeragePipelineCommissionFee).deleteIn([
      'individualSplits',
      index,
    ])
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _deleteBonus(index: number) {
    const newValue = (this.props
      .value as BrokeragePipelineCommissionFee).deleteIn(['bonuses', index])
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _getTotalDistributed() {
    let total = this.props.value.companySplit || 0

    this.props.value.individualSplits.toArray().forEach((ind) => {
      total += ind.amount
    })

    return total
  }

  private _onChangeBonusContact(index: number, newContact?: AssociatedContact) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).setIn(
      ['bonuses', index, 'contact'],
      newContact,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _onChangeBonusAmount(index: number, newAmount: number) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).setIn(
      ['bonuses', index, 'amount'],
      newAmount,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _onChangeCompanySplit(companySplit: number) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).set(
      'companySplit',
      companySplit,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _onChangeIndividualSplitContact(
    index: number,
    newContact?: AssociatedContact,
  ) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).setIn(
      ['individualSplits', index, 'contact'],
      newContact,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _onChangeIndividualSplitAmount(index: number, newAmount: number) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).setIn(
      ['individualSplits', index, 'amount'],
      newAmount,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }

  private _onChangeTotalFeePercent(totalFee: number) {
    const newValue = (this.props.value as BrokeragePipelineCommissionFee).set(
      'totalFeePercent',
      totalFee,
    )
    this.props.onChange(newValue as BrokeragePipelineCommissionFee)
  }
}
